import React, { useEffect, useState } from "react";
import { api, API, getToken } from "@/lib/api";
import { toast } from "sonner";
import {
    Users, Loader2, Link2, ChevronDown, ChevronUp, Activity, Pill,
    CalendarCheck, FolderOpen, Heart, History, Lock,
} from "lucide-react";

/** Care Navigator Workspace — permission-filtered family records for orgs. */
export default function OrgFamiliesWorkspace({ orgId }) {
    const [families, setFamilies] = useState(null);
    const [code, setCode] = useState("");
    const [connecting, setConnecting] = useState(false);
    const [openId, setOpenId] = useState(null);
    const [detail, setDetail] = useState(null);

    const load = async () => {
        try {
            const r = await api.get(`/org/workspace/${orgId}/families`);
            setFamilies(r.data.families || []);
        } catch { setFamilies([]); }
    };
    useEffect(() => { setOpenId(null); setDetail(null); load(); /* eslint-disable-next-line */ }, [orgId]);

    const connect = async () => {
        if (!code.trim()) return;
        setConnecting(true);
        try {
            const r = await api.post(`/org/workspace/${orgId}/connect`, { code: code.trim() });
            toast.success(`Connected with ${r.data.family_name}. ${r.data.note || ""}`);
            setCode("");
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't connect — check the code.");
        } finally { setConnecting(false); }
    };

    const openFamily = async (userId) => {
        if (openId === userId) { setOpenId(null); setDetail(null); return; }
        setOpenId(userId);
        setDetail(null);
        try {
            const r = await api.get(`/org/workspace/${orgId}/family/${userId}`);
            setDetail(r.data);
        } catch (e) {
            setDetail({ error: e?.response?.data?.detail || "Access unavailable." });
        }
    };

    return (
        <section className="rounded-3xl border border-[#E2E1D8] bg-white p-6 md:p-8" data-testid="org-families-workspace">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <h2 className="text-lg font-heading font-semibold text-[#1F2624] flex items-center gap-2">
                        <Users size={18} strokeWidth={1.7} /> Families — Care Navigator Workspace
                    </h2>
                    <p className="text-sm text-[#4C5551] mt-1 max-w-2xl">
                        Families own their record. You see exactly what each family has chosen to share — read-only,
                        with author and timestamp. Ask a family for their Memory Lane connect code to link up.
                    </p>
                </div>
            </div>

            <div className="mt-4 flex gap-2 items-center flex-wrap">
                <input value={code} onChange={e => setCode(e.target.value)} placeholder="Family connect code (e.g. ML-A1B2C3)"
                    className="ml-input !py-2 text-sm max-w-xs" data-testid="org-connect-code-input" />
                <button type="button" onClick={connect} disabled={connecting || !code.trim()} data-testid="org-connect-btn"
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-2xl text-sm font-semibold bg-teal text-white hover:bg-teal-dark disabled:opacity-50">
                    {connecting ? <Loader2 size={14} className="animate-spin" /> : <Link2 size={14} />} Connect a family
                </button>
            </div>

            {families === null ? (
                <p className="mt-4 text-sm text-[#6E7A74]">Loading families…</p>
            ) : families.length === 0 ? (
                <p className="mt-4 text-sm text-[#4C5551]" data-testid="org-families-empty">
                    No families connected yet.
                </p>
            ) : (
                <div className="mt-4 space-y-3">
                    {families.map(f => (
                        <div key={f.user_id} className="border border-cream-dark rounded-2xl" data-testid={`org-family-${f.user_id}`}>
                            <button type="button" onClick={() => openFamily(f.user_id)}
                                className="w-full flex items-center justify-between gap-3 p-4 text-left"
                                data-testid={`org-family-open-${f.user_id}`}>
                                <div>
                                    <p className="text-sm font-semibold text-[#1F2624]">{f.name}</p>
                                    <p className="text-xs text-[#6E7A74] mt-0.5">
                                        {f.status === "paused" ? "⏸ Access paused by family" : `Sharing ${f.shared_count} item${f.shared_count === 1 ? "" : "s"}`}
                                        {f.connected_at ? ` · connected ${new Date(f.connected_at).toLocaleDateString()}` : ""}
                                    </p>
                                </div>
                                {openId === f.user_id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                            </button>
                            {openId === f.user_id && (
                                <div className="border-t border-cream-dark p-4" data-testid={`org-family-detail-${f.user_id}`}>
                                    {!detail ? (
                                        <p className="text-sm text-[#6E7A74]">Loading shared record…</p>
                                    ) : detail.error ? (
                                        <p className="text-sm text-coral-dark">{detail.error}</p>
                                    ) : (
                                        <FamilyDetail detail={detail} orgId={orgId} />
                                    )}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </section>
    );
}

function FamilyDetail({ detail, orgId }) {
    const s = detail.sections || {};
    const nothing = Object.keys(s).length === 0;
    const openSharedDoc = async (docId) => {
        try {
            const r = await fetch(`${API}/org/intel/${orgId}/document/${docId}`, {
                headers: { Authorization: `Bearer ${getToken()}` },
            });
            if (!r.ok) throw new Error();
            const blob = await r.blob();
            window.open(URL.createObjectURL(blob), "_blank");
        } catch { toast.error("Couldn't open the document."); }
    };
    return (
        <div className="space-y-4">
            {nothing && (
                <p className="text-sm text-[#4C5551] inline-flex items-center gap-1.5" data-testid="org-family-nothing-shared">
                    <Lock size={14} /> This family hasn't enabled any shared information yet. They control access from their Settings.
                </p>
            )}
            {s.behaviors && (
                <div data-testid="org-section-behaviors">
                    <p className="text-xs font-semibold uppercase tracking-wide text-[#6E7A74] flex items-center gap-1.5"><Activity size={13} /> Behaviors — last 30 days ({s.behaviors.last_30_days_total})</p>
                    <div className="mt-1.5 flex gap-1.5 flex-wrap">
                        {Object.entries(s.behaviors.by_type || {}).map(([t, n]) => (
                            <span key={t} className="px-2.5 py-1 rounded-2xl bg-cream-dark/60 text-xs text-[#4C5551]">{t} × {n}</span>
                        ))}
                    </div>
                    <ul className="mt-2 space-y-1">
                        {(s.behaviors.recent || []).slice(0, 6).map((b, i) => (
                            <li key={i} className="text-xs text-[#4C5551]">
                                <strong className="text-[#1F2624]">{b.type}</strong>
                                {b.severity ? ` · severity ${b.severity}` : ""}{b.time_of_day ? ` · ${b.time_of_day}` : ""}
                                {b.notes ? ` — ${b.notes}` : ""} <span className="text-[#6E7A74]">({(b.date || "").slice(0, 10)} · {b.author})</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            {s.medications && (
                <div data-testid="org-section-medications">
                    <p className="text-xs font-semibold uppercase tracking-wide text-[#6E7A74] flex items-center gap-1.5"><Pill size={13} /> Medications</p>
                    <ul className="mt-1.5 space-y-0.5">
                        {(s.medications.list || []).map((m, i) => (
                            <li key={i} className="text-xs text-[#4C5551]">
                                <strong className="text-[#1F2624]">{m.name}</strong>{m.dose ? ` ${m.dose}` : ""}{m.frequency ? ` · ${m.frequency}` : ""}
                                {(m.status || "active") === "stopped" ? " · STOPPED" : ""}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            {s.appointments && (
                <div data-testid="org-section-appointments">
                    <p className="text-xs font-semibold uppercase tracking-wide text-[#6E7A74] flex items-center gap-1.5"><CalendarCheck size={13} /> Appointment summaries</p>
                    <ul className="mt-1.5 space-y-1.5">
                        {s.appointments.slice(0, 4).map((a, i) => (
                            <li key={i} className="text-xs text-[#4C5551]">
                                <strong className="text-[#1F2624]">{a.provider || "Visit"}</strong>{a.date ? ` · ${a.date}` : ""}
                                {a.recent_changes ? ` — ${a.recent_changes}` : ""}
                                {(a.questions || []).length ? ` · ${a.questions.length} saved question(s)` : ""}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            {s.documents && (
                <div data-testid="org-section-documents">
                    <p className="text-xs font-semibold uppercase tracking-wide text-[#6E7A74] flex items-center gap-1.5"><FolderOpen size={13} /> Shared documents</p>
                    {s.documents.length === 0 ? (
                        <p className="mt-1.5 text-xs text-[#4C5551]">The family hasn't shared any individual documents yet — they select each one from their Document Passport.</p>
                    ) : (
                        <ul className="mt-1.5 space-y-1">
                            {s.documents.map((d, i) => (
                                <li key={i} className="text-xs text-[#4C5551] flex items-center gap-2">
                                    <span>{d.kind || "Document"}{d.filename ? ` — ${d.filename}` : ""}</span>
                                    {d.id && (
                                        <button type="button" onClick={() => openSharedDoc(d.id)} data-testid={`org-doc-open-${d.id}`}
                                            className="text-teal-dark font-semibold hover:underline">Open</button>
                                    )}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
            {s.wellness && (
                <div data-testid="org-section-wellness">
                    <p className="text-xs font-semibold uppercase tracking-wide text-[#6E7A74] flex items-center gap-1.5"><Heart size={13} /> Caregiver wellness trend (28d, {s.wellness.check_ins_28d} check-ins)</p>
                    <div className="mt-1.5 flex gap-1.5 flex-wrap">
                        {Object.entries(s.wellness.domain_averages || {}).map(([d, v]) => (
                            <span key={d} className="px-2.5 py-1 rounded-2xl bg-cream-dark/60 text-xs text-[#4C5551]">{d}: {v}/10</span>
                        ))}
                    </div>
                    <p className="text-[11px] text-[#6E7A74] mt-1">{s.wellness.note}</p>
                </div>
            )}
            {s.timeline && (
                <div data-testid="org-section-timeline">
                    <p className="text-xs font-semibold uppercase tracking-wide text-[#6E7A74] flex items-center gap-1.5"><History size={13} /> Journey timeline ({s.timeline.length} events)</p>
                    <ul className="mt-1.5 space-y-1 max-h-64 overflow-y-auto pr-2">
                        {s.timeline.map((e, i) => (
                            <li key={i} className="text-xs text-[#4C5551]">
                                <span className="text-[#6E7A74]">{(e.date || "").slice(0, 10)}</span> · <strong className="text-[#1F2624]">{e.what}</strong>
                                {e.detail ? ` — ${e.detail}` : ""} <span className="text-[#6E7A74]">({e.added_by})</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}
